/**
 * @file Menu.js
 * @module src/js/components/Menu
 * @description
 * **Navigation menu component for the EDALab frontend.**
 * - Displays different menu items and submenus based on user authentication status.
 * - Includes links to various sections of the website such as About, Research, Photos, External Links, and Member Zone.
 * - Provides login/logout functionality.
 * - Adapts to touch screen devices with a close button.
 *
 * @version
 * <p>
 *  2025-11-25: First version with documentation. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-11-29: More readable formatting in drop-down content. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Add `ExternalLink` icons to and adjust styles. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Disable logout button when logging out. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Migrate to use useHandleLogout hook. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Show last login time in the menu for logged-in users. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2026-07-14: Remove legacy website in dropdown menu. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 * </p>
 */
import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useUser } from '../contexts/UserProvider';
import useHandleLogout from '../hooks/useHandleLogout';
import SubMenu from './SubMenu';
import DropDownMenu from './DropDownMenu';
import { Info } from 'lucide-react'
import getDevice from '../functions/getDevice';

export default function Menu({ handleMouseLeaveMenu, menuRef, setIsMenuVisible }) {
    const { uid, isLoggingOut, lastLogin } = useUser();
    const handleLogout = useHandleLogout();
    const [isSubmenuVisible, setIsSubmenuVisible] = useState(new Array(5).fill(false));
    const [hoverMenuItem, setHoverMenuItem] = useState(new Array(7).fill(false));
    const { hasTouchScreen } = getDevice();

    const spanStyle = useMemo(() => ({
        display: 'flex', // full flex row
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%', // span takes full menu width
    }), []);

    const baseAnchorStyle = useMemo(() => ({
        cursor: 'pointer',
        transition: 'all .2s ease-in-out',
    }), []);

    const hoverStyle = useMemo(() => ({
        color: '#fffbc0ff',
        transform: 'translateY(-1px) scale(1.02)', // lift + micro-scale
        textShadow: '0 2px 14px rgba(255,247,130,.5)', // soft glow
    }), []);

    const handleMouseEnterLeaveMenuItem = useCallback((index, isEnter) => {
        setHoverMenuItem(prev => {
            const newHover = [...prev];
            newHover[index] = isEnter;
            return newHover;
        });
    }, []);

    const closeMenuAndSubmenus = useCallback(() => {
        setIsMenuVisible(false);
        setIsSubmenuVisible(prev => new Array(prev.length).fill(false));
    }, [setIsMenuVisible, setIsSubmenuVisible]);

    return (
        <div id="menu" ref={menuRef} onMouseLeave={handleMouseLeaveMenu}>
            <ul>
                <li style={{display: 'flex', justifyContent: 'space-between'}}>
                    <h1>
                        Welcome,<br />
                        {uid === "Guest" ? "Guest" :
                            <Link to="/member-zone/profile"
                                onClick={closeMenuAndSubmenus}
                            >{uid}</Link>
                        }!
                    </h1>
                    <DropDownMenu
                        element={<Info size={30} />}
                        dropDownContent={[
                            'EDA Lab: 網站首頁',
                            'About: 實驗室介紹、指導教授、成員列表',
                            'Research: 計畫、學術發表、程式分享',
                            'Photos: 活動照片、網頁橫幅照片',
                            'External Links: 外部連結',
                            'Member Zone: 成員專區(需登入)',
                            'Log In/Out: 成員登入、登出'
                        ].join('<br/>')} // Use HTML line breaks for spacing
                        top="60%"
                        left="-350%"
                    />
                </li>
                <li>
                    <Link to="/" onClick={closeMenuAndSubmenus}
                        onMouseEnter={() => handleMouseEnterLeaveMenuItem(0, true)}
                        onMouseLeave={() => handleMouseEnterLeaveMenuItem(0, false)}
                        style={{ ...baseAnchorStyle, ...(hoverMenuItem[0] ? hoverStyle : {}) }}
                    >EDA Lab</Link>
                </li>
                <SubMenu
                    submenuIndex={0}
                    title="About"
                    hrefs={["https://cc.ee.ntu.edu.tw/~ywchang/EDA_lab.pdf", "/advisor", "/members"]}
                    subTitles={["Lab Introduction", "Advisor", "Members"]}
                    isSubmenuVisible={isSubmenuVisible}
                    setIsSubmenuVisible={setIsSubmenuVisible}
                    setIsMenuVisible={setIsMenuVisible}
                    hoverMenuItem={hoverMenuItem}
                    handleMouseEnterLeaveMenuItem={handleMouseEnterLeaveMenuItem}
                    menuIndex={1}
                    baseAnchorStyle={baseAnchorStyle}
                    hoverStyle={hoverStyle}
                    spanStyle={spanStyle}
                />
                <SubMenu
                    submenuIndex={1}
                    title="Research"
                    hrefs={["/projects", "/publications", "/downloads"]}
                    subTitles={["Projects", "Publications", "Downloads"]}
                    isSubmenuVisible={isSubmenuVisible}
                    setIsSubmenuVisible={setIsSubmenuVisible}
                    setIsMenuVisible={setIsMenuVisible}
                    hoverMenuItem={hoverMenuItem}
                    handleMouseEnterLeaveMenuItem={handleMouseEnterLeaveMenuItem}
                    menuIndex={2}
                    baseAnchorStyle={baseAnchorStyle}
                    hoverStyle={hoverStyle}
                    spanStyle={spanStyle}
                />
                <SubMenu
                    submenuIndex={2}
                    title="Photos"
                    hrefs={["/activity-photo-gallery", "/banner-photo-gallery"]}
                    subTitles={["Activity Photo Gallery","Banner Photo Gallery"]}
                    isSubmenuVisible={isSubmenuVisible}
                    setIsSubmenuVisible={setIsSubmenuVisible}
                    setIsMenuVisible={setIsMenuVisible}
                    hoverMenuItem={hoverMenuItem}
                    handleMouseEnterLeaveMenuItem={handleMouseEnterLeaveMenuItem}
                    menuIndex={3}
                    baseAnchorStyle={baseAnchorStyle}
                    hoverStyle={hoverStyle}
                    spanStyle={spanStyle}
                />
                <SubMenu
                    submenuIndex={3}
                    title="External Links"
                    hrefs={["http://140.112.20.242/users/sign_in", "http://edaunion.ee.ntu.edu.tw/ws_list.htm"]}
                    subTitles={["GitLab (NTU IP Required)", "EDA Union Servers"]}
                    isSubmenuVisible={isSubmenuVisible}
                    setIsSubmenuVisible={setIsSubmenuVisible}
                    setIsMenuVisible={setIsMenuVisible}
                    hoverMenuItem={hoverMenuItem}
                    handleMouseEnterLeaveMenuItem={handleMouseEnterLeaveMenuItem}
                    menuIndex={4}
                    baseAnchorStyle={baseAnchorStyle}
                    hoverStyle={hoverStyle}
                    spanStyle={spanStyle}
                />
                {uid === "Guest" ? (
                    <li>
                        <Link to="/login" onClick={closeMenuAndSubmenus}
                            onMouseEnter={() => handleMouseEnterLeaveMenuItem(5, true)}
                            onMouseLeave={() => handleMouseEnterLeaveMenuItem(5, false)}
                            style={{ ...baseAnchorStyle, ...(hoverMenuItem[5] ? hoverStyle : {}) }}
                        >Member Zone 🔑</Link>
                    </li>
                ) : (
                    <SubMenu
                        submenuIndex={4}
                        title="Member Zone"
                        hrefs={["profile", "contact", "archives", "lab-data", "paper-review", "new-member"].map(
                            href => `/member-zone/${href}`
                        )}
                        subTitles={["Profile", "Contact", "Archives", "Lab Data", "Paper Review", "New Member"]}
                        isSubmenuVisible={isSubmenuVisible}
                        setIsSubmenuVisible={setIsSubmenuVisible}
                        setIsMenuVisible={setIsMenuVisible}
                        hoverMenuItem={hoverMenuItem}
                        handleMouseEnterLeaveMenuItem={handleMouseEnterLeaveMenuItem}
                        menuIndex={5}
                        baseAnchorStyle={baseAnchorStyle}
                        hoverStyle={hoverStyle}
                        spanStyle={spanStyle}
                    />
                )}
                <li>
                    {uid === "Guest" ? (
                        <Link to="/login" onClick={() => {
                                closeMenuAndSubmenus();
                                handleMouseEnterLeaveMenuItem(6, false);
                            }}
                            onMouseEnter={() => handleMouseEnterLeaveMenuItem(6, true)}
                            onMouseLeave={() => handleMouseEnterLeaveMenuItem(6, false)}
                            style={{ ...baseAnchorStyle, ...(hoverMenuItem[6] ? hoverStyle : {}) }}
                        >Log In 🔑</Link>
                    ) : (
                        <div
                            onClick={async () => {
                                if (isLoggingOut) return;
                                await handleLogout();
                                closeMenuAndSubmenus();
                                handleMouseEnterLeaveMenuItem(6, false);
                            }}
                            onMouseEnter={() => handleMouseEnterLeaveMenuItem(6, true)}
                            onMouseLeave={() => handleMouseEnterLeaveMenuItem(6, false)}
                            style={{
                                ...baseAnchorStyle,
                                cursor: isLoggingOut ? 'not-allowed' : 'pointer',
                                opacity: isLoggingOut ? 0.4 : 1,
                                ...(hoverMenuItem[6] ? hoverStyle : {})
                            }}
                        >Log Out 🔐</div>
                    )}
                </li>
            </ul>
            {uid !== "Guest" && lastLogin !== "N/A" && (
                <div
                    style={{
                        marginTop: '100%',
                        paddingTop: 10,
                        borderTop: '1px solid rgba(255,255,255,0.12)',
                        fontSize: 12,
                        color: '#9aa0a6',
                    }}
                >
                    Last login: {new Date(lastLogin).toLocaleString()}
                </div>
            )}
            {hasTouchScreen && <div href="#menu" className="close" onClick={closeMenuAndSubmenus} />}
        </div>
    );
}
